import type { Turn } from './direction';

/**
 * How a mower is allowed to change heading. Each mower picks one; this is what gives each mower
 * its own steering "grammar" (docs/GDD.md §4).
 */
export type TurnRuleSpec =
  /** 90° turns any time; no reversing, unless `canReverse` (the Reverse Gear upgrade). */
  | { kind: 'free90'; canReverse?: boolean }
  /** 90° turns any time; a 180° pull-back that then needs `cooldownTicks` to recharge. */
  | { kind: 'free90Pullback'; cooldownTicks: number }
  /**
   * 90° turns, but a turn in the same direction as the previous turn requires having traveled at
   * least `sameDirectionMinTiles` tiles since that turn (turning radius). With 2, a U-turn needs a
   * 3-wide lane. No reversing, unless `canReverse` (the Reverse Gear upgrade).
   */
  | { kind: 'turnSpacing'; sameDirectionMinTiles: number; canReverse?: boolean }
  /**
   * Any turn including spinning around in place; it can also park on a tile and spin there until
   * steered (press and hold).
   */
  | { kind: 'freeSpin' };

/** The integer bookkeeping turn rules need, stored on the mower. */
export interface TurnRuleState {
  /** Most recent left/right turn, or null if none since spawn/bounce. */
  lastTurn: 'left' | 'right' | null;
  /** Whole tiles traveled since the last left/right turn. */
  tilesSinceTurn: number;
  /** Ticks until a pull-back is available again. */
  pullbackCooldown: number;
}

export function initialTurnRuleState(): TurnRuleState {
  return { lastTurn: null, tilesSinceTurn: 0, pullbackCooldown: 0 };
}

export function canTurn(rule: TurnRuleSpec, state: Readonly<TurnRuleState>, turn: Turn): boolean {
  switch (rule.kind) {
    case 'free90':
      return turn !== 'reverse' || rule.canReverse === true;
    case 'free90Pullback':
      return turn !== 'reverse' || state.pullbackCooldown === 0;
    case 'turnSpacing':
      if (turn === 'reverse') return rule.canReverse === true;
      return turn !== state.lastTurn || state.tilesSinceTurn >= rule.sameDirectionMinTiles;
    case 'freeSpin':
      return true;
  }
}

/** Updates rule state after a player turn has been applied. */
export function recordTurn(rule: TurnRuleSpec, state: TurnRuleState, turn: Turn): void {
  if (turn === 'reverse') {
    if (rule.kind === 'free90Pullback') state.pullbackCooldown = rule.cooldownTicks;
    return;
  }
  state.lastTurn = turn;
  state.tilesSinceTurn = 0;
}

/** Forced reversal after a crash is not a player turn: it clears turn history. */
export function recordBounce(state: TurnRuleState): void {
  state.lastTurn = null;
  state.tilesSinceTurn = 0;
}

export function tickTurnRule(state: TurnRuleState): void {
  if (state.pullbackCooldown > 0) state.pullbackCooldown--;
}

export function recordTileTraveled(state: TurnRuleState): void {
  state.tilesSinceTurn++;
}

/** What the mower's turn rule currently forbids, for on-screen explanation. */
export interface TurnRuleIndicators {
  /** Pull-back readiness in [0, 1] (1 = ready), or null if the mower has no pull-back. */
  pullbackCharge: number | null;
  /** The turn direction temporarily locked by a turning radius, if any. */
  lockedTurn: 'left' | 'right' | null;
  /** Tile centers still to pass before `lockedTurn` unlocks (0 when nothing is locked). */
  lockTilesLeft: number;
}

export function turnRuleIndicators(
  rule: TurnRuleSpec,
  state: Readonly<TurnRuleState>,
): TurnRuleIndicators {
  const none: TurnRuleIndicators = { pullbackCharge: null, lockedTurn: null, lockTilesLeft: 0 };
  switch (rule.kind) {
    case 'free90Pullback':
      return { ...none, pullbackCharge: 1 - state.pullbackCooldown / rule.cooldownTicks };
    case 'turnSpacing': {
      const left = rule.sameDirectionMinTiles - state.tilesSinceTurn;
      if (state.lastTurn === null || left <= 0) return none;
      return { ...none, lockedTurn: state.lastTurn, lockTilesLeft: left };
    }
    case 'free90':
    case 'freeSpin':
      return none;
  }
}
